import { DEFAULT_ITEM_ID_COLUMN_NAME } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { sortNewDerivedFeaturesByDependencies } from '@/tools/models/templateFormatting.utilities';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import { TypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1String';
import { SeeqNames } from '@seeqdev/constants';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type {
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  TemplateDerivedFeature,
  TemplateInput,
} from '@/tools/models/template.types';
import type { ColumnRuleOutputV1 } from '@seeqdev/api/model/ColumnRuleOutputV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';

/**
 * The fields of a feature the template already has (input, intermediate or output) that a rule needs to reference it
 * by inputFeatureId. Built by getExistingFeaturesByColumnId; this is data, not a check.
 */
export type ExistingColumnFeature = { id: string; name: string; description?: string };

/**
 * Maps table column ids to existing template features using their columnDefinition.
 * Features without a bound column are excluded.
 *
 * Used by computeTemplateInputsAndIntermediatesFromTable to avoid recreating existing features
 * and their dependencies, and to reference them by inputFeatureId.
 */
export const getExistingFeaturesByColumnId = (
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): Map<string, ExistingColumnFeature> => {
  const featuresByColumnId = new Map<string, ExistingColumnFeature>();
  [...existingInputFeatures, ...existingDerivedFeatures].forEach((feature) => {
    const columnId = feature.columnDefinition?.id;
    if (columnId && feature.id) {
      featuresByColumnId.set(columnId, { id: feature.id, name: feature.name, description: feature.description });
    }
  });
  return featuresByColumnId;
};

/**
 * Resolves a column ID from a formulaCreator rule's inputs array to the correct
 * TemplateRuleInputV1Object reference. Columns bound to an existing template feature become inputFeatureId
 * references; new input columns become inputFeatureIndex references; new intermediates and outputs become
 * derivedFeatureIndex references.
 *
 * @internal Exported for testing
 */
export const resolveRuleInput = (
  columnId: string,
  paramName: string,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingFeaturesByColumnId: ReadonlyMap<string, ExistingColumnFeature> = new Map(),
): TemplateRuleInputV1Object => {
  const existingFeature = existingFeaturesByColumnId.get(columnId);
  if (existingFeature) {
    return {
      name: existingFeature.name,
      description: existingFeature.description ?? '',
      type: TypeEnum.FEATUREID,
      inputFeatureId: existingFeature.id,
    };
  }

  const inputFeatureIndex = newInputFeatures.findIndex((f) => f.referenceId === columnId);
  if (inputFeatureIndex !== -1) {
    const inputFeature = newInputFeatures[inputFeatureIndex];
    return {
      name: inputFeature.name,
      description: inputFeature.description ?? '',
      type: TypeEnum.FEATUREID,
      inputFeatureIndex,
    };
  }

  const derivedIndex = newDerivedFeatures.findIndex((f) => (f.referenceId ?? f.id) === columnId);
  if (derivedIndex !== -1) {
    const derivedFeature = newDerivedFeatures[derivedIndex];
    return {
      name: derivedFeature.name,
      description: '',
      type: TypeEnum.FEATUREID,
      derivedFeatureIndex: derivedIndex,
    };
  }

  return {
    name: paramName,
    type: TypeEnum.ITEMID,
    argument: columnId,
  };
};

/**
 * Splits a pipe-delimited string into an array of trimmed, non-empty strings.
 * Returns an empty array if the input is not a string.
 */
const splitPipeDelimited = (value: unknown): string[] =>
  typeof value === 'string'
    ? value
        .split('|')
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
    : [];

/**
 * Converts a column-side formulaCreator ColumnRuleOutputV1 into the FeatureRuleV1 shape
 * expected by the template API. Only formulaCreator rules are supported for scaling table
 * template outputs.
 *
 * Pre-populating formulaCreator here allows fetchFormulasForFeatures to short-circuit
 * the formula fetch for these features, since it skips features that already have
 * rules[0].formulaCreator set. Returns undefined if required arguments are missing,
 * allowing downstream logic to fall back to fetching formulas.
 *
 * @internal Exported for testing
 */
export const columnRuleToFeatureRule = (
  columnRule: ColumnRuleOutputV1,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingFeaturesByColumnId: ReadonlyMap<string, ExistingColumnFeature> = new Map(),
): FeatureRuleV1 | undefined => {
  const { arguments: args, inputs = [] } = columnRule;

  if (!args?.formula || !args?.name) {
    return undefined;
  }

  const paramNames = args.parameters && args.parameters.trim() ? args.parameters.split('|').map((p) => p.trim()) : [];
  const variableParameterNames = splitPipeDelimited(args.variableParameters);
  const variableParameterStringValues = splitPipeDelimited(args.variableParameterStrings);
  const ruleInputs: TemplateRuleInputV1Object[] = inputs.map((columnId, i) =>
    resolveRuleInput(
      columnId,
      paramNames[i] ?? columnId,
      newInputFeatures,
      newDerivedFeatures,
      existingFeaturesByColumnId,
    ),
  );

  return {
    inputs: ruleInputs,
    formulaCreator: {
      formula: { argument: args.formula, name: 'formula', type: TypeEnum.TEXT },
      name: { argument: args.name, name: 'name', type: TypeEnum.TEXT },
      parameters: { argument: paramNames, name: 'parameters', type: TypeEnum.TEXT },
      variableParameters: { argument: variableParameterNames, name: 'variableParameters', type: TypeEnum.TEXT },
      variableParameterStrings: {
        argument: variableParameterStringValues,
        name: 'variableParameterStrings',
        type: TypeEnum.TEXT,
      },
      ...(args.scopedTo && {
        scopedTo: { argument: args.scopedTo, name: 'scopedTo', type: TypeEnum.ITEMID },
      }),
    },
  };
};

export const toTemplateInputsFromTable = (
  ids: string[],
  inputs: NewlyAddedInputFeature[],
  columnIdMap: Map<string, ColumnDefinition>,
): NewlyAddedInputFeature[] => {
  const existingById = new Map((inputs ?? []).map((i) => [i.referenceId, i]));

  return ids
    .map((id) => {
      const existing = existingById.get(id);
      if (existing) {
        return existing;
      }

      const col = columnIdMap.get(id);
      if (!col) {
        return null;
      }

      return {
        referenceId: col.id,
        id: col.id,
        name: col.displayName,
        isCollapsed: false,
        description: col.description || '',
        originalName: col.displayName,
      };
    })
    .filter((input): input is NewlyAddedInputFeature => !!input);
};

/**
 * Converts a column's primary and fallback rules to FeatureRuleV1 entries, preserving their order.
 * This ensures inputs referenced only by fallback formulas aren't treated as unused.
 */
export const columnRulesToFeatureRules = (
  column: ColumnDefinition,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingFeaturesByColumnId: ReadonlyMap<string, ExistingColumnFeature> = new Map(),
): FeatureRuleV1[] =>
  (column.rules ?? [])
    .map((rule) => columnRuleToFeatureRule(rule, newInputFeatures, newDerivedFeatures, existingFeaturesByColumnId))
    .filter((rule): rule is FeatureRuleV1 => !!rule);

export const toTemplateIntermediatesFromTable = (
  ids: string[],
  columnIdMap: Map<string, ColumnDefinition>,
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] =>
  ids
    .map((id) => columnIdMap.get(id))
    .filter((c): c is ColumnDefinition => !!c)
    .map((column) => ({
      referenceId: column.id,
      id: column.id,
      name: column.displayName,
      isOutput: false,
      outputType: OutputTypeEnum.ITEMID,
      outputItemType: column.outputItemType,
      outputItemMetadata: column.outputItemMetadata,
      rules: columnRulesToFeatureRules(column, newInputFeatures, newDerivedFeatures),
    }));

/** Validates that a column can be used as a template output. Only columns with rules of type 'formulaCreator' are
 *  supported in the backend, for now. */
export const isValidOutputColumn = (column: ColumnDefinition): boolean => {
  const validRules = [SeeqNames.MaterializedTables.Rules.FormulaCreator];
  const rules = column.rules ?? [];

  if (rules.length === 0) return false;
  return rules.every((rule) =>
    validRules.includes(rule.rule as typeof SeeqNames.MaterializedTables.Rules.FormulaCreator),
  );
};

/**
 * Recursively finds all input and intermediate column IDs for a given starting column ID.
 * Only includes DEFAULT_ITEM_ID_COLUMN_NAME if it is a direct input to an output.
 * Columns in existingFeatureColumnIds are bound to features the template already has, so they are neither collected
 * nor walked into.
 */
export const getInputAndIntermediateIds = (
  startId: string,
  visitedIds: string[],
  inputs: string[],
  intermediates: string[],
  outputIds: string[],
  columnIdMap: Map<string, ColumnDefinition>,
  isDirectInputToOutput = false,
  existingFeatureColumnIds: ReadonlySet<string> = new Set(),
) => {
  if (visitedIds.includes(startId)) {
    return;
  }
  visitedIds.push(startId);

  if (existingFeatureColumnIds.has(startId) && !outputIds.includes(startId)) {
    return;
  }

  const col = columnIdMap.get(startId);
  if (!col) {
    return;
  }

  // If this column is already classified as an input or intermediate from a previous output traversal,
  // don't recurse into its dependencies again. This prevents incorrectly exploring dependencies of
  // non-creator rule columns that have already been identified as inputs.
  if (inputs.includes(startId) || intermediates.includes(startId)) {
    return;
  }

  const directInputs: string[] = [];
  const isSearchItemsDisplayName = (name: string) => name === DEFAULT_ITEM_ID_COLUMN_NAME;
  col.rules?.forEach((r) => r.inputs?.forEach((inp) => directInputs.push(inp)));

  if (directInputs.length === 0) {
    // Only include DEFAULT_ITEM_ID_COLUMN_NAME if it's a direct input to an output
    if (
      !inputs.includes(startId) &&
      !outputIds.includes(startId) &&
      (!isSearchItemsDisplayName(col.displayName) || isDirectInputToOutput)
    ) {
      inputs.push(startId);
    }
    return;
  }

  if (
    !isValidOutputColumn(col) &&
    !outputIds.includes(startId) &&
    !inputs.includes(startId) &&
    (!isSearchItemsDisplayName(col.displayName) || isDirectInputToOutput)
  ) {
    // Non-creator rule (e.g., findDescendant, itemSearch, getItemProperty) - add as input and stop recursing.
    // Model inputs should only come from columns that are not creator rules, and we should not walk into
    // their dependencies since those are implementation details of how the finder rule works.
    inputs.push(startId);
    return;
  } else if (!outputIds.includes(startId) && !intermediates.includes(startId) && isValidOutputColumn(col)) {
    intermediates.push(startId);
  }

  // Only recurse into dependencies for columns with formula creator rules
  // For each direct input, pass true if the current column is an output
  directInputs.forEach((depId) =>
    getInputAndIntermediateIds(
      depId,
      visitedIds,
      inputs,
      intermediates,
      outputIds,
      columnIdMap,
      outputIds.includes(startId),
      existingFeatureColumnIds,
    ),
  );
};

/**
 * Given a set of output columns, recursively finds all input and intermediate columns,
 * then builds NewlyAddedInputFeature[] and NewlyAddedDerivedFeature[] for use with
 * createAndPublishTemplate. Uses a two-pass approach — stubs are built first to establish
 * the full derived feature index order, then rules are applied in a second pass so that
 * cross-references between outputs and intermediates resolve to correct derivedFeatureIndex
 * values.
 *
 * When editing a template, pass the columns bound to features it already has (see getExistingFeaturesByColumnId).
 * Those columns are not added as new inputs or intermediates; rules reference their features by inputFeatureId.
 *
 * Pure computation — no store writes. See getTemplateInputsAndIntermediatesFromTable in
 * templateColumn.actions.ts for the store-writing wrapper.
 */
export const computeTemplateInputsAndIntermediatesFromTable = (
  outputColumns: ColumnDefinition[],
  currentInputs: NewlyAddedInputFeature[],
  allColumns: ColumnDefinition[],
  existingFeaturesByColumnId: ReadonlyMap<string, ExistingColumnFeature> = new Map(),
): {
  newInputFeatures: NewlyAddedInputFeature[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
} => {
  const columnIdMap = new Map<string, ColumnDefinition>(allColumns.map((c) => [c.id, c]));
  const outputIds = outputColumns.map((c) => c.id);
  const existingFeatureColumnIds = new Set(existingFeaturesByColumnId.keys());

  const inputIds: string[] = [];
  const intermediateIds: string[] = [];
  outputColumns.forEach((col) => {
    getInputAndIntermediateIds(
      col.id,
      [],
      inputIds,
      intermediateIds,
      outputIds,
      columnIdMap,
      false,
      existingFeatureColumnIds,
    );
  });

  const newInputFeatures = toTemplateInputsFromTable(inputIds, currentInputs, columnIdMap);

  // Build stubs without rules first so we have full index context for rule resolution
  const outputStubs: NewlyAddedDerivedFeature[] = outputColumns.map((column) => ({
    referenceId: column.id,
    id: column.id,
    name: column.displayName,
    isOutput: true,
    outputType: OutputTypeEnum.ITEMID,
    outputItemType: column.outputItemType,
    outputItemMetadata: column.outputItemMetadata,
    rules: [],
  }));

  const intermediateStubs: NewlyAddedDerivedFeature[] = intermediateIds
    .map((id) => columnIdMap.get(id))
    .filter((c): c is ColumnDefinition => !!c)
    .map((column) => ({
      referenceId: column.id,
      id: column.id,
      name: column.displayName,
      isOutput: false,
      outputType: OutputTypeEnum.ITEMID,
      outputItemType: column.outputItemType,
      outputItemMetadata: column.outputItemMetadata,
      rules: [],
    }));

  // Full derived features array for index resolution — outputs first to match final array order
  const allDerivedStubs = [...outputStubs, ...intermediateStubs];

  // Second pass: apply rules with full context
  const newOutputFeatures = outputStubs.map((stub, i) => ({
    ...stub,
    rules: columnRulesToFeatureRules(outputColumns[i], newInputFeatures, allDerivedStubs, existingFeaturesByColumnId),
  }));

  const newIntermediateFeatures = intermediateStubs.map((stub) => {
    const column = columnIdMap.get(stub.referenceId!)!;
    return {
      ...stub,
      rules: columnRulesToFeatureRules(column, newInputFeatures, allDerivedStubs, existingFeaturesByColumnId),
    };
  });

  return {
    newInputFeatures,
    newDerivedFeatures: sortNewDerivedFeaturesByDependencies([...newOutputFeatures, ...newIntermediateFeatures]),
  };
};
